import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, createCommand, $isElementNode } from 'lexical';

// Create custom indent commands
export const INCREASE_INDENT_COMMAND = createCommand('INCREASE_INDENT_COMMAND');
export const DECREASE_INDENT_COMMAND = createCommand('DECREASE_INDENT_COMMAND');

const INDENT_UNIT = 40; // pixels per indent level

const IndentPlugin = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Register the increase indent command
    const removeIncreaseIndentListener = editor.registerCommand(
      INCREASE_INDENT_COMMAND,
      () => {
        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return;
          }

          // Get all unique paragraph nodes to indent
          const nodesToIndent = new Set();
          const selectedNodes = selection.getNodes();

          selectedNodes.forEach((node) => {
            let targetNode = node;
            const parent = node.getParent();

            // If the node is a text node, use its parent paragraph
            if (parent && $isElementNode(parent) && parent.getType() === 'paragraph') {
              targetNode = parent;
            } else if (node.getType() === 'paragraph') {
              targetNode = node;
            }

            if (targetNode && targetNode.getType() === 'paragraph') {
              nodesToIndent.add(targetNode);
            }
          });

          // Apply indent using Lexical's native setIndent method
          nodesToIndent.forEach((node) => {
            const currentIndent = node.getIndent();
            node.setIndent(currentIndent + 1);

            // Apply visual styling via inline style
            const dom = editor.getElementByKey(node.getKey());
            if (dom) {
              dom.style.paddingLeft = `${(currentIndent + 1) * INDENT_UNIT}px`;
              dom.setAttribute('data-indent', (currentIndent + 1).toString());
            }
          });
        });

        return true;
      },
      4 // COMMAND_PRIORITY_NORMAL
    );

    // Register the decrease indent command
    const removeDecreaseIndentListener = editor.registerCommand(
      DECREASE_INDENT_COMMAND,
      () => {
        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return;
          }

          // Get all unique paragraph nodes to outdent
          const nodesToOutdent = new Set();
          const selectedNodes = selection.getNodes();

          selectedNodes.forEach((node) => {
            let targetNode = node;
            const parent = node.getParent();

            // If the node is a text node, use its parent paragraph
            if (parent && $isElementNode(parent) && parent.getType() === 'paragraph') {
              targetNode = parent;
            } else if (node.getType() === 'paragraph') {
              targetNode = node;
            }

            if (targetNode && targetNode.getType() === 'paragraph') {
              nodesToOutdent.add(targetNode);
            }
          });

          // Apply outdent using Lexical's native setIndent method
          nodesToOutdent.forEach((node) => {
            const currentIndent = node.getIndent();
            const newIndent = Math.max(0, currentIndent - 1);
            node.setIndent(newIndent);

            // Apply visual styling via inline style
            const dom = editor.getElementByKey(node.getKey());
            if (dom) {
              if (newIndent > 0) {
                dom.style.paddingLeft = `${newIndent * INDENT_UNIT}px`;
                dom.setAttribute('data-indent', newIndent.toString());
              } else {
                dom.style.paddingLeft = '';
                dom.removeAttribute('data-indent');
              }
            }
          });
        });

        return true;
      },
      4 // COMMAND_PRIORITY_NORMAL
    );

    return () => {
      removeIncreaseIndentListener();
      removeDecreaseIndentListener();
    };
  }, [editor]);

  return null;
};

export default IndentPlugin;
